import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export const StepUpDialog = ({ open, onOpenChange, onVerified }) => {
  const { stepUp } = useAuth();
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await stepUp(code.trim(), method);
      toast.success("Verified — continuing");
      setCode("");
      onOpenChange(false);
      onVerified?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Invalid code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="step-up-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" /> Confirm it's you
          </DialogTitle>
          <DialogDescription>
            For your security, this action needs a fresh two-factor code.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="step-up-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="stepup-code">{method === "backup" ? "Backup code" : "Authenticator code"}</Label>
            <Input
              id="stepup-code"
              placeholder={method === "backup" ? "XXXX-XXXX" : "123456"}
              value={code}
              onChange={(e) => setCode(method === "backup" ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
              maxLength={method === "backup" ? 9 : 6}
              className="text-center text-lg tracking-[0.3em]"
              autoFocus
              data-testid="step-up-code-input"
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="text-xs text-stone-500 underline"
              onClick={() => { setMethod(method === "totp" ? "backup" : "totp"); setCode(""); setError(""); }}
              data-testid="step-up-toggle-method"
            >
              {method === "totp" ? "Use a backup code instead" : "Use authenticator app instead"}
            </button>
            <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800 rounded-full" disabled={submitting || !code} data-testid="step-up-verify-btn">
              {submitting ? "Verifying…" : "Verify"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
